import React, { useMemo } from 'react';
import { Chip, ListItemIcon, Stack, Typography, createFilterOptions } from '@mui/material';
import { UserAvatar } from '@/components/UserAvatar';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';

const getUserLabel = (user) => user.name ?? user.email ?? '';

// Typing (when searchable) matches name or email.
const filterUsers = createFilterOptions({
	stringify: (user) => `${user.name ?? ''} ${user.email ?? ''}`,
});

const UserOptionContent = ({ user, hideAvatar, displayField, markInactive }) => {
	const showAdditionalField = displayField && user[displayField];
	return (
		<Stack direction='row' width='100%' alignItems='center'>
			{!hideAvatar && (
				<ListItemIcon sx={{ minWidth: '3em' }}>
					<UserAvatar userId={user.id} />
				</ListItemIcon>
			)}
			<Stack
				direction='row'
				width='100%'
				alignItems='center'
				{...(showAdditionalField && { justifyContent: 'space-between' })}
			>
				<Typography
					sx={{
						flex: 1,
						minWidth: 0,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{getUserLabel(user)}
				</Typography>
				{showAdditionalField && (
					<Typography variant='task_updated' sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
						{user[displayField]}
					</Typography>
				)}
				{/* Deactivated by a Folio admin */}
				{markInactive && user.isActive === false && (
					<Chip size='small' label='Inactive' sx={{ ml: 1, flexShrink: 0 }} />
				)}
			</Stack>
		</Stack>
	);
};

/**
 * Select for users, with no data of its own: ResponsiveAutocomplete with user labels
 * (name, else email), avatars in options and chips (each UserAvatar reads its own user by
 * id), and an optional extra field
 * (displayField) at the end of each option. With markInactive, a deactivated user
 * (isActive false) gets an "Inactive" chip at the end of their option (for filters, which
 * still list them). Options are sorted by label, deactivated users last. value/onChange are user ids, as in
 * ResponsiveAutocomplete. Used by OwnerAutocomplete (CRM users) and AdminUserPicker (all
 * users).
 */
export const UserAutocomplete = ({
	options = [],
	hideAvatar = false,
	displayField,
	markInactive = false,
	...props
}) => {
	const sortedOptions = useMemo(
		() =>
			[...options].sort(
				(a, b) =>
					(a.isActive === false) - (b.isActive === false) ||
					getUserLabel(a).localeCompare(getUserLabel(b)),
			),
		[options],
	);

	return (
		<ResponsiveAutocomplete
			options={sortedOptions}
			getOptionLabel={getUserLabel}
			filterOptions={filterUsers}
			renderOptionContent={(user) => (
				<UserOptionContent {...{ user, hideAvatar, displayField, markInactive }} />
			)}
			renderSingleValue={(user) => (
				<UserOptionContent {...{ user, hideAvatar, displayField, markInactive }} />
			)}
			{...(!hideAvatar && {
				getChipProps: (user) => ({ avatar: <UserAvatar userId={user.id} /> }),
			})}
			fitMenuToContent
			{...props}
		/>
	);
};

UserAutocomplete.displayName = 'UserAutocomplete';
